import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useLocation } from "@/lib/router";
import { useLang } from "@/context/LanguageContext";
import { http } from "@/lib/storeApi";
import { SEO_BASE } from "@/i18n/seo";
import { splitLang, withLang } from "@/lib/i18nPath";
import { applyHreflang } from "@/components/RouteSeo";
import { ProductCard } from "@/components/store/ProductCard";
import NotFound from "@/pages/NotFound";

export const TYPE_LABEL = {
  category: { it: "Categorie", en: "Categories", de: "Kategorien", es: "Categorías", fr: "Catégories" },
  format: { it: "Collezioni", en: "Collections", de: "Kollektionen", es: "Colecciones", fr: "Collections" },
  olfactory: { it: "Fragranze", en: "Fragrances", de: "Düfte", es: "Fragancias", fr: "Parfums" },
  seasonal: { it: "Collezioni stagionali", en: "Seasonal collections", de: "Saisonale Kollektionen", es: "Colecciones de temporada", fr: "Collections saisonnières" },
  special: { it: "Special Edition", en: "Special Edition", de: "Special Edition", es: "Special Edition", fr: "Special Edition" },
  landing: { it: "Scopri", en: "Discover", de: "Entdecken", es: "Descubre", fr: "Découvrir" },
  journal: { it: "Journal", en: "Journal", de: "Journal", es: "Journal", fr: "Journal" },
};
const PARENT = { journal: "/journal", landing: "/explore" };
export const fmtJournalDate = (d, lang) => (d ? new Date(d).toLocaleDateString({ it: "it-IT", en: "en-GB", de: "de-DE", es: "es-ES", fr: "fr-FR" }[lang] || "it-IT", { day: "numeric", month: "long", year: "numeric" }) : "");
const CONCLUDED = { it: "Edizione conclusa", en: "Edition concluded", de: "Edition beendet", es: "Edición concluida", fr: "Édition terminée" };
const RELATED = { it: "Scopri anche", en: "Discover also", de: "Entdecken Sie auch", es: "Descubre también", fr: "Découvrez aussi" };

const Body = ({ text }) =>
  (text || "").split(/\n{2,}/).filter(Boolean).map((b, i) =>
    b.startsWith("## ")
      ? <h2 key={i} className="mt-12 font-display text-2xl font-light text-gold">{b.slice(3)}</h2>
      : <p key={i} className="mt-5 text-base leading-relaxed text-white/65">{b}</p>
  );

const Products = ({ items }) => ((items || []).length > 0 ? (
  <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-products">
    {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
  </div>
) : null);

const CatalogLd = ({ page, url, lang, t }) => {
  const crumbs = [
    { "@type": "ListItem", position: 1, name: "VORNX", item: SEO_BASE + withLang("/", lang) },
    { "@type": "ListItem", position: 2, name: t(TYPE_LABEL[page.type]), item: SEO_BASE + withLang(PARENT[page.type] || "/products", lang) },
    { "@type": "ListItem", position: 3, name: page.h1, item: url },
  ];
  const img = page.cover ? (page.cover.startsWith("/") ? SEO_BASE + page.cover : page.cover) : undefined;
  const ld = page.type === "journal" ? [
    { "@context": "https://schema.org", "@type": "Article", headline: page.h1, description: page.seo_description, url, inLanguage: lang, image: img,
      datePublished: page.published_at, dateModified: page.updated_at, author: { "@type": "Organization", name: page.author || "VORNX" },
      publisher: { "@id": `${SEO_BASE}/#organization` }, mainEntityOfPage: url },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs },
  ] : [
    { "@context": "https://schema.org", "@type": "CollectionPage", name: page.h1, description: page.seo_description, url, inLanguage: lang,
      ...(page.cover ? { image: page.cover.startsWith("/") ? SEO_BASE + page.cover : page.cover } : {}),
      isPartOf: { "@id": `${SEO_BASE}/#website` },
      mainEntity: { "@type": "ItemList", itemListElement: (page.products || []).map((p, i) => ({ "@type": "ListItem", position: i + 1, url: SEO_BASE + withLang(`/shop/${p.slug}`, lang), name: t(p.name) })) } },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs },
  ];
  if ((page.faq || []).length) ld.push({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: page.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
  return <Helmet>{ld.map((x, i) => <script key={i} type="application/ld+json">{JSON.stringify(x)}</script>)}</Helmet>;
};

const CatalogPage = ({ page }) => {
  const { lang, t } = useLang();
  const url = SEO_BASE + withLang(page.paths[lang], lang);
  useEffect(() => applyHreflang(Object.fromEntries(Object.entries(page.paths).map(([l, p]) => [l, SEO_BASE + withLang(p, l)]))), [page]);

  return (
    <main data-testid={`catalog-page-${page.type}`} className="bg-ink pb-24 pt-32">
      <Helmet>
        <title>{page.seo_title}</title>
        <meta name="description" content={page.seo_description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={page.seo_title} />
        <meta property="og:description" content={page.seo_description} />
        <meta property="og:url" content={url} />
        {page.cover && <meta property="og:image" content={page.cover.startsWith("/") ? SEO_BASE + page.cover : page.cover} />}
      </Helmet>
      <CatalogLd page={page} url={url} lang={lang} t={t} />
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <nav aria-label="breadcrumb" className="text-xs uppercase tracking-widest text-white/40" data-testid="catalog-breadcrumb">
          <Link to="/" className="hover:text-gold">VORNX</Link> · <Link to={PARENT[page.type] || "/products"} className="hover:text-gold">{t(TYPE_LABEL[page.type])}</Link> · <span className="text-white/60">{page.h1}</span>
        </nav>
        {page.status === "concluded" && <span data-testid="catalog-concluded" className="mt-6 inline-block border border-gold/40 px-3 py-1 text-[10px] uppercase tracking-widest text-gold">{t(CONCLUDED)}</span>}
        <h1 data-testid="catalog-h1" className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight text-gold sm:text-5xl lg:text-6xl">{page.h1}</h1>
        {page.type === "journal" && <p data-testid="journal-meta" className="mt-4 text-xs uppercase tracking-widest text-white/40">{page.author} · {fmtJournalDate(page.published_at, lang)}</p>}
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">{page.intro}</p>
        {page.cover && <img src={page.cover} alt={page.cover_alt} className="mt-12 aspect-[16/9] w-full object-cover" data-testid="catalog-cover" />}
        {page.type !== "journal" && <Products items={page.products} />}
        <div className="mx-auto max-w-3xl"><Body text={page.body} /></div>
        {page.type === "journal" && <Products items={page.products} />}
        {(page.faq || []).length > 0 && (
          <div className="mx-auto mt-16 max-w-3xl space-y-8" data-testid="catalog-faq">
            {page.faq.map((f, i) => (
              <div key={i} className="border-l border-gold/30 pl-6">
                <h3 className="font-display text-xl text-gold">{f.q}</h3>
                <p className="mt-3 text-white/60">{f.a}</p>
              </div>
            ))}
          </div>
        )}
        {(page.related || []).length > 0 && (
          <div className="mt-16" data-testid="catalog-related">
            <p className="text-xs uppercase tracking-overline text-gold">{t(RELATED)}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {page.related.map((r) => (
                <Link key={r.path} to={r.path} className="border border-white/15 px-5 py-3 text-sm text-white/70 transition-colors hover:border-gold hover:text-gold">{r.h1}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

// Unknown public paths: try the Admin-managed catalog before showing 404.
export const CatalogResolver = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const { base } = splitLang(pathname);
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    setState({ loading: true });
    http.get("/catalog/resolve", { params: { path: base, lang } })
      .then((r) => setState({ page: r.data }))
      .catch(() => setState({ missing: true }));
  }, [base, lang]);

  if (state.loading) return <main className="min-h-screen bg-ink" data-testid="catalog-loading" />;
  if (state.missing) return <NotFound />;
  if (state.page.redirect) return <Navigate to={state.page.redirect} replace />;
  return <CatalogPage page={state.page} />;
};

// Published catalog pages for menus/footers (internal links).
export const useCatalogLinks = () => {
  const { lang } = useLang();
  const [links, setLinks] = useState([]);
  useEffect(() => { http.get("/catalog/public", { params: { lang } }).then((r) => setLinks(r.data.filter((c) => c.type !== "journal"))).catch(() => setLinks([])); }, [lang]);
  return links;
};
